import { useTranslation } from "react-i18next";

import type { GongChannel } from "../../application/useGongChannel";
import {
    GONG_CLUSTER_CHOICES,
    GONG_MODES,
    GONG_SPAN_CHOICES,
} from "../../domain/meetingGong";

/**
 * How the gong is set up: one sound, or an opening and a closing with a
 * stretch of time between them.
 *
 * Four things, and the one that decides the shape comes first. The span is
 * absent rather than disabled while the answer is "just one", because a
 * single gong has no length and an input that does nothing is worse than no
 * input; the closing goes with it, since there is no end to mark on something
 * with no middle.
 *
 * Real radios for the mode: it is one choice out of two, arrow keys should
 * move between them, and a native fieldset gives that with no keyboard
 * handler of our own. Native selects for the numbers, for the same reason.
 */
export default function MeetingGongSettings({
    channel,
}: {
    channel: GongChannel;
}) {
    const { t } = useTranslation();
    const { plan, setPlan } = channel;
    const timed = plan.mode === "timed";

    return (
        <div className="flex flex-col gap-2 p-1" data-testid="gong-settings">
            <fieldset>
                <legend className="px-1 pb-1 text-xs text-room-ink-muted">
                    {t("meetingGong.mode")}
                </legend>

                {GONG_MODES.map((mode) => (
                    <label
                        key={mode}
                        className={`room-menu-item ${
                            plan.mode === mode ? "bg-room-active" : ""
                        }`}
                    >
                        <input
                            type="radio"
                            name="gong-mode"
                            value={mode}
                            checked={plan.mode === mode}
                            onChange={() => setPlan({ ...plan, mode })}
                            data-testid={`gong-mode-${mode}`}
                            className="size-4 accent-[var(--room-accent)]"
                        />
                        <span>{t(`meetingGong.modes.${mode}`)}</span>
                    </label>
                ))}
            </fieldset>

            {timed && (
                <label className="flex flex-col gap-1 text-xs text-room-ink-muted">
                    {t("meetingGong.span")}
                    <select
                        className="room-field"
                        data-testid="gong-span"
                        value={plan.spanMinutes}
                        onChange={(event) =>
                            setPlan({
                                ...plan,
                                spanMinutes: Number(event.target.value),
                            })
                        }
                    >
                        {GONG_SPAN_CHOICES.map((minutes) => (
                            <option key={minutes} value={minutes}>
                                {t("meetingGong.minutes", { count: minutes })}
                            </option>
                        ))}
                    </select>
                </label>
            )}

            <label className="flex flex-col gap-1 text-xs text-room-ink-muted">
                {timed ? t("meetingGong.open") : t("meetingGong.openSingle")}
                <select
                    className="room-field"
                    data-testid="gong-open"
                    value={plan.openStrikes}
                    onChange={(event) =>
                        setPlan({ ...plan, openStrikes: Number(event.target.value) })
                    }
                >
                    {GONG_CLUSTER_CHOICES.map((sounds) => (
                        <option key={sounds} value={sounds}>
                            {t("meetingGong.times", { count: sounds })}
                        </option>
                    ))}
                </select>
            </label>

            {timed && (
                <label className="flex flex-col gap-1 text-xs text-room-ink-muted">
                    {t("meetingGong.close")}
                    <select
                        className="room-field"
                        data-testid="gong-close"
                        value={plan.closeStrikes}
                        onChange={(event) =>
                            setPlan({
                                ...plan,
                                closeStrikes: Number(event.target.value),
                            })
                        }
                    >
                        {GONG_CLUSTER_CHOICES.map((sounds) => (
                            <option key={sounds} value={sounds}>
                                {t("meetingGong.times", { count: sounds })}
                            </option>
                        ))}
                    </select>
                </label>
            )}
        </div>
    );
}
